import { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Loader2, Trash2, ShieldAlert } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Tiered delete confirmation dialog used platform-wide.
 *
 * Tier 1 — low stakes (questions, items, resources): single yes/no modal.
 * Tier 2 — mid stakes (LEXPs, tracks, surveys, lesson plans): modal lists
 *          dependency counts fetched from /api/dependencies/{type}/{id}
 *          and requires an explicit "Delete permanently" click.
 * Tier 3 — high stakes (learners, cohorts, orgs): user must re-enter their
 *          own email + password. Wrong credentials → nothing happens.
 *          Set `realm="owner"` on the Owner portal to hit
 *          /api/owner/auth/verify-password instead of /api/auth/verify-password.
 *
 * The dialog never performs the delete itself — it calls `onConfirm()` once
 * all gates pass. The caller is responsible for the actual DELETE request.
 */
export default function DeleteConfirmDialog({
  open,
  onOpenChange,
  tier = 1,
  entityName,
  entityType,
  entityId,
  realm = "admin",
  title,
  description,
  confirmLabel,
  onConfirm,
}) {
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");

  // Reset state whenever the dialog opens/closes.
  useEffect(() => {
    if (!open) {
      setBusy(false);
      setEmail("");
      setPassword("");
      setAuthError("");
    }
  }, [open]);

  const close = () => {
    if (busy) return;
    onOpenChange(false);
  };

  const runConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (_) {
      // Caller surfaces its own error toast — keep the dialog open so the
      // user can retry or cancel.
    } finally {
      setBusy(false);
    }
  };

  const runReauthAndConfirm = async (e) => {
    e.preventDefault();
    setAuthError("");
    if (!email.trim() || !password) {
      setAuthError("Enter your email and password");
      return;
    }
    setBusy(true);
    try {
      const path = realm === "owner"
        ? `${API}/owner/auth/verify-password`
        : `${API}/auth/verify-password`;
      await axios.post(
        path,
        { email: email.trim(), password },
        { withCredentials: true },
      );
    } catch (_) {
      setAuthError("Incorrect email or password");
      setBusy(false);
      return;
    }
    // Credentials verified — proceed with the actual delete.
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (_) {
      // Caller handles its error toast
    } finally {
      setBusy(false);
    }
  };

  const resolvedTitle = title
    || (tier === 3 ? "Confirm deletion with your password" : "Delete permanently");
  const resolvedConfirmLabel = confirmLabel || "Delete permanently";
  const isTier3 = tier === 3;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="bg-[#11141A] border-white/10 text-white max-w-md"
        data-testid={`delete-confirm-tier-${tier}`}
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className={`mt-0.5 rounded-lg p-2 shrink-0 ${isTier3 ? "bg-red-500/15" : tier === 2 ? "bg-amber-500/15" : "bg-red-500/15"}`}>
              {isTier3 ? (
                <ShieldAlert className="h-5 w-5 text-red-400" />
              ) : tier === 2 ? (
                <AlertTriangle className="h-5 w-5 text-amber-400" />
              ) : (
                <Trash2 className="h-5 w-5 text-red-400" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <DialogTitle className="font-body font-bold text-lg text-white">
                {resolvedTitle}
              </DialogTitle>
              <DialogDescription className="text-[#A1A9B8] font-body text-sm mt-1">
                {description || (
                  <>
                    Permanently delete <span className="text-white font-medium">"{entityName}"</span>?
                    This cannot be undone.
                  </>
                )}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Tier 3 — re-auth form */}
        {isTier3 && (
          <form onSubmit={runReauthAndConfirm} className="mt-2 space-y-3">
            <div className="rounded-lg border border-red-500/20 bg-red-500/[0.04] px-3.5 py-3">
              <div className="text-xs font-body uppercase tracking-wider text-red-400/80 mb-1">
                High-risk action
              </div>
              <p className="text-sm text-[#E8EAED] font-body">
                Enter your account credentials to confirm deletion of
                {" "}<span className="text-white font-medium">"{entityName}"</span>.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[#A1A9B8] font-body text-sm">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                autoComplete="off"
                data-testid="reauth-email-input"
                className="bg-[#0A0C10] border-white/10 text-white h-10"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#A1A9B8] font-body text-sm">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                data-testid="reauth-password-input"
                className="bg-[#0A0C10] border-white/10 text-white h-10"
              />
            </div>

            {authError && (
              <div className="text-sm font-body text-red-400" data-testid="reauth-error">
                {authError}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="ghost"
                onClick={close}
                disabled={busy}
                data-testid="delete-confirm-cancel"
                className="text-[#A1A9B8] hover:text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy}
                data-testid="delete-confirm-submit"
                className="h-10 px-5 bg-red-600 hover:bg-red-700 text-white font-body font-medium rounded-lg"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
                {resolvedConfirmLabel}
              </Button>
            </div>
          </form>
        )}

        {/* Tier 1 + Tier 2 — action buttons */}
        {!isTier3 && (
          <div className="flex justify-end gap-2 pt-3">
            <Button
              type="button"
              variant="ghost"
              onClick={close}
              disabled={busy}
              data-testid="delete-confirm-cancel"
              className="text-[#A1A9B8] hover:text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={runConfirm}
              disabled={busy}
              data-testid="delete-confirm-submit"
              className="h-10 px-5 bg-red-600 hover:bg-red-700 text-white font-body font-medium rounded-lg"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
              {resolvedConfirmLabel}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
